import type { SupabaseClient } from "@supabase/supabase-js";
import type { UserRoleProfile } from "~/composables/useUserRole";

function profileRowKey(row: UserRoleProfile): string {
  return String(row.user_id || row.id || row.email || "");
}

/**
 * Load profile row(s) for the signed-in auth user.
 * Matches user_id, legacy id column, and login email (fixes duplicate rows in Supabase).
 */
export async function fetchProfilesForAuthUser(
  supabase: SupabaseClient,
  authUserId: string,
  authEmail?: string | null,
): Promise<UserRoleProfile[]> {
  const merged = new Map<string, UserRoleProfile>();

  const { data: byAuthId, error: byAuthIdError } = await supabase
    .from("profiles")
    .select("*")
    .or(`user_id.eq.${authUserId},id.eq.${authUserId}`);

  if (byAuthIdError) {
    throw byAuthIdError;
  }

  for (const row of (byAuthId || []) as UserRoleProfile[]) {
    merged.set(profileRowKey(row), row);
  }

  const email = authEmail?.trim();
  if (email) {
    const { data: byEmail, error: byEmailError } = await supabase
      .from("profiles")
      .select("*")
      .ilike("email", email);

    if (byEmailError) {
      throw byEmailError;
    }

    for (const row of (byEmail || []) as UserRoleProfile[]) {
      merged.set(profileRowKey(row), row);
    }
  }

  return [...merged.values()];
}

/**
 * Pick the profile row that belongs to this login.
 * Email match wins over user_id-only rows (common when an old adminDev row shares the auth id).
 */
export function pickAuthProfile(
  rows: UserRoleProfile[],
  authUserId: string,
  authEmail?: string | null,
): UserRoleProfile | null {
  if (!rows.length) return null;

  const emailNorm = authEmail?.trim().toLowerCase() || "";
  let best: UserRoleProfile | null = null;
  let bestScore = -1;
  let bestUpdated = "";

  for (const row of rows) {
    let score = 0;
    if (emailNorm && row.email?.trim().toLowerCase() === emailNorm) score += 1000;
    if (row.user_id === authUserId) score += 100;
    if (row.id === authUserId) score += 50;

    const updated = row.updated_at || "";
    if (score > bestScore || (score === bestScore && updated > bestUpdated)) {
      bestScore = score;
      best = row;
      bestUpdated = updated;
    }
  }

  return best;
}

export async function resolveAuthProfile(
  supabase: SupabaseClient,
  authUserId: string,
  authEmail?: string | null,
): Promise<UserRoleProfile | null> {
  const rows = await fetchProfilesForAuthUser(supabase, authUserId, authEmail);
  return pickAuthProfile(rows, authUserId, authEmail);
}

/** Link email-matched profile to auth user when user_id was missing or stale. */
export async function syncAuthProfileUserId(
  supabase: SupabaseClient,
  profile: UserRoleProfile,
  authUserId: string,
  authEmail?: string | null,
): Promise<void> {
  if (profile.user_id === authUserId) return;

  const emailNorm = authEmail?.trim().toLowerCase();
  const profileEmail = profile.email?.trim().toLowerCase();
  if (!emailNorm || profileEmail !== emailNorm) return;

  const filter = profile.user_id
    ? { column: "user_id" as const, value: profile.user_id }
    : profile.id
      ? { column: "id" as const, value: profile.id }
      : null;

  if (!filter) return;

  const { error } = await supabase
    .from("profiles")
    .update({ user_id: authUserId })
    .eq(filter.column, filter.value);

  if (error) {
    console.warn("[resolveAuthProfile] sync user_id failed:", error.message);
  }
}
